import { LearningStats, LearningSession } from '@/features/courses/types';
import StorageService from './StorageService';

class LearningStatsService {
  private readonly STATS_KEY = 'learningStats';
  private readonly SESSIONS_KEY = 'learningSessions';

  // Get current learning stats with defaults
  async getLearningStats(): Promise<LearningStats> {
    const defaultStats: LearningStats = {
      currentStreak: 0,
      bestStreak: 0,
      dailyGoal: 3, // Default to 3 lectures per day
      todayProgress: 0,
      lastLearningDate: '',
      totalLecturesWatched: 0,
      learningDays: [],
    };

    const stats = await StorageService.getItem(this.STATS_KEY, defaultStats);
    return stats || defaultStats;
  }

  // Save learning stats
  async saveLearningStats(stats: LearningStats): Promise<boolean> {
    return await StorageService.setItem(this.STATS_KEY, stats);
  }

  // Get today's date as ISO string (YYYY-MM-DD)
  private getTodayDateString(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Check if two dates are consecutive days
  private areConsecutiveDays(date1: string, date2: string): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1;
  }

  // Calculate current streak
  private calculateCurrentStreak(learningDays: string[]): number {
    if (learningDays.length === 0) return 0;

    const sortedDays = [...learningDays].sort().reverse(); // Most recent first
    const today = this.getTodayDateString();
    
    // Check if today or yesterday was a learning day
    const mostRecentDay = sortedDays[0];
    const daysDiff = Math.floor(
      (new Date(today).getTime() - new Date(mostRecentDay).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    // If more than 1 day has passed since last learning, streak is broken
    if (daysDiff > 1) return 0;

    let streak = 1;
    for (let i = 1; i < sortedDays.length; i++) {
      if (this.areConsecutiveDays(sortedDays[i], sortedDays[i - 1])) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  }

  // Record a lecture watch and update stats
  async recordLectureWatch(courseId: string): Promise<LearningStats> {
    const stats = await this.getLearningStats();
    const today = this.getTodayDateString();

    // Update total lectures watched
    stats.totalLecturesWatched++;

    // Update today's progress
    if (stats.lastLearningDate === today) {
      stats.todayProgress++;
    } else {
      stats.todayProgress = 1;
      stats.lastLearningDate = today;
      
      // Add today to learning days if not already added
      if (!stats.learningDays.includes(today)) {
        stats.learningDays.push(today);
      }
    }

    // Recalculate streaks
    stats.currentStreak = this.calculateCurrentStreak(stats.learningDays);
    stats.bestStreak = Math.max(stats.bestStreak, stats.currentStreak);

    // Save updated stats
    await this.saveLearningStats(stats);

    // Record the session
    await this.recordLearningSession(courseId);

    return stats;
  }

  // Update daily goal
  async updateDailyGoal(newGoal: number): Promise<LearningStats> {
    const stats = await this.getLearningStats();
    stats.dailyGoal = Math.max(1, newGoal); // Minimum 1 lecture
    await this.saveLearningStats(stats);
    return stats;
  }

  // Get today's progress towards goal
  async getTodayProgress(): Promise<{ progress: number; goal: number; percentage: number }> {
    const stats = await this.getLearningStats();
    const today = this.getTodayDateString();
    
    const progress = stats.lastLearningDate === today ? stats.todayProgress : 0;
    const percentage = Math.min(100, Math.round((progress / stats.dailyGoal) * 100));
    
    return {
      progress,
      goal: stats.dailyGoal,
      percentage,
    };
  }

  // Check if daily goal is achieved
  async isDailyGoalAchieved(): Promise<boolean> {
    const { progress, goal } = await this.getTodayProgress();
    return progress >= goal;
  }

  // Reset today's progress (for testing or day rollover)
  async resetTodayProgress(): Promise<LearningStats> {
    const stats = await this.getLearningStats();
    const today = this.getTodayDateString();
    
    if (stats.lastLearningDate !== today) {
      stats.todayProgress = 0;
    }
    
    await this.saveLearningStats(stats);
    return stats;
  }

  // Record a learning session
  private async recordLearningSession(courseId: string): Promise<void> {
    const sessions: LearningSession[] = (await StorageService.getItem(this.SESSIONS_KEY, [])) || [];
    const today = this.getTodayDateString();
    
    const todaySession = sessions.find(s => s.date === today);
    
    if (todaySession) {
      todaySession.lecturesWatched++;
      if (!todaySession.coursesStudied.includes(courseId)) {
        todaySession.coursesStudied.push(courseId);
      }
    } else {
      sessions.push({
        date: today,
        lecturesWatched: 1,
        coursesStudied: [courseId],
      });
    }
    
    await StorageService.setItem(this.SESSIONS_KEY, sessions);
  }
}

const learningStatsService = new LearningStatsService();
export default learningStatsService; 